import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { DropdownService, ZoneService } from '@core/services';
import { profileStore } from '@core/store';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { ApiResponse, Dropdown } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Zone, ZoneCreate, ZoneUpdate } from '@core/types/zone.type';

/**
 * Query service para operaciones de Zonas (grid + CRUD + dropdown)
 * Scopeda al condominio activo vía `X-Condominium-Id` (interceptor)
 */
@Injectable({ providedIn: 'root' })
export class ZoneQuery {
  private readonly dropdownService = inject(DropdownService);
  private readonly zoneService = inject(ZoneService);
  private readonly profileStore = inject(profileStore);
  private readonly queryClient = inject(QueryClient);

  useActiveZonesDropdown() {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      return {
        queryKey: QUERY_KEYS.dropdowns.zones(condominiumId),
        queryFn: () => lastValueFrom(this.dropdownService.zones()),
        select: (data: ApiResponse<Dropdown<string>[]>) => data.data || [],
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useZonesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const c = config();
      return {
        queryKey: [...QUERY_KEYS.zones.grid(c), condominiumId],
        queryFn: () => lastValueFrom(this.zoneService.grid(c)),
        select: (res: ApiResponse<PaginationResult<Zone>>) => res.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useCreateZone() {
    return injectMutation(() => ({
      mutationFn: (data: ZoneCreate) => lastValueFrom(this.zoneService.create(data)),
      onSuccess: () => {
        const condominiumId = this.profileStore.data.activeCondominiumId();
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.zones.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dropdowns.zones(condominiumId) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.condominiums.details() });
      },
    }));
  }

  useUpdateZone() {
    return injectMutation(() => ({
      mutationFn: (data: ZoneUpdate) => lastValueFrom(this.zoneService.update(data)),
      onSuccess: () => {
        const condominiumId = this.profileStore.data.activeCondominiumId();
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.zones.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dropdowns.zones(condominiumId) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.condominiums.details() });
      },
    }));
  }

  useDeleteZone() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.zoneService.delete(id)),
      onSuccess: () => {
        const condominiumId = this.profileStore.data.activeCondominiumId();
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.zones.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dropdowns.zones(condominiumId) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.condominiums.details() });
      },
    }));
  }
}

